import type { MetricOption } from '@/entities/bi'
import type { ChartConfig } from '@/shared/components/ui/chart'

import React, { useMemo, useState } from 'react'
import { formatMetricValue, useMoneyMetrics } from '@/pages/dashboard/lib/money'
import { format } from 'date-fns'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'

import { DateAggregation, zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { intlFormat } from '@/shared/tools/dates'
import { NumberFormats } from '@/shared/tools/locale'

import MetricTooltipRow from '../../MetricTooltipRow'

export default function Chart2({
	mainMetric,
	additionalMetrics,
	aggregation,
	data,
	metrics,
}: {
	data: {
		meta: string[]
		data: unknown[][]
	}
	mainMetric: string
	additionalMetrics: string[]
	aggregation: DateAggregation
	metrics: MetricOption[]
}) {
	const currency = useSelectedCurrency()
	const moneyMetrics = useMoneyMetrics()
	const [hoveringDataKey, setHoveringDataKey] = useState<string | undefined>(
		undefined
	)
	const chartData = useMemo(
		() =>
			zipSeries(data?.meta, data?.data).map((data) => ({
				...data,
				date: data[aggregation],
			})),
		[data, aggregation]
	)
	const chartConfig = Object.fromEntries(
		metrics?.map((metric) => [
			metric.value,
			{
				label: metric.label,
				color: metric.color,
			},
		]) || []
	) satisfies ChartConfig

	const handleMouseEnter = (payload: { value?: string }) => {
		if (payload.value === undefined) return
		setHoveringDataKey(payload.value)
	}

	const handleMouseLeave = () => {
		setHoveringDataKey(undefined)
	}
	const enabledMetrics = useMemo(
		() =>
			metrics?.filter(
				(metric) =>
					additionalMetrics.includes(metric.value) ||
					mainMetric === metric.value
			),
		[metrics, additionalMetrics, mainMetric]
	)

	return (
		<ChartContainer config={chartConfig} className='aspect-[1/0.35]'>
			<LineChart
				accessibilityLayer={false}
				data={chartData}
				margin={{
					left: 24,
					right: 24,
				}}
			>
				{/* recharts v3 CartesianGrid defaults to yAxisId=0, but every
				YAxis here is keyed by metric.value, so the default lookup
				finds no axis and horizontal ticks come back empty. Point it
				at the visible axis explicitly. */}
				<CartesianGrid
					vertical={false}
					strokeDasharray='3 3'
					yAxisId={mainMetric}
				/>
				<XAxis
					axisLine={false}
					dataKey='date'
					interval='preserveStartEnd'
					angle={DateAggregation.DateKey === aggregation ? -45 : 0}
					tickMargin={12}
					niceTicks='auto'
					tickFormatter={(value) => {
						switch (aggregation) {
							case DateAggregation.DateKey:
								return format(value, 'MMM d')
							case DateAggregation.WeekKey:
								return format(value, 'w')
							case DateAggregation.MonthKey:
								return format(value, 'MMM')
							default:
								return format(value, 'MMM d')
						}
					}}
				/>

				{metrics?.map((metric) => (
					<YAxis
						hide={mainMetric !== metric.value}
						key={metric.value}
						yAxisId={metric.value}
						id={metric.value}
						// recharts v3 reserves the default 60px width for every
						// registered YAxis (hidden or not) when positioning sibling
						// axes, even though only non-hidden axes consume offset
						// space. Without width=0 here, hidden axes push the visible
						// one off the plot area (see recharts es6/state/selectors/
						// axisSelectors.js selectAllYAxesOffsetSteps).
						width={mainMetric === metric.value ? undefined : 0}
						tickFormatter={(value) =>
							formatMetricValue(
								value,
								metric.value,
								currency,
								moneyMetrics,
								NumberFormats.Compact
							)
						}
						tickLine={false}
						axisLine={false}
					/>
				))}
				<ChartTooltip
					cursor={false}
					content={
						<ChartTooltipContent
							labelFormatter={(value) =>
								typeof value === 'string' || typeof value === 'number'
									? intlFormat(new Date(value))
									: ''
							}
							formatter={(v, name, item) => (
								<MetricTooltipRow
									color={item.color}
									label={String(chartConfig[name as string]?.label ?? name)}
									value={formatMetricValue(
										Number(v),
										String(name),
										currency,
										moneyMetrics,
										NumberFormats.Standart
									)}
								/>
							)}
						/>
					}
				/>
				<ChartLegend
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onClick={handleMouseEnter}
					content={<ChartLegendContent />}
				/>
				{enabledMetrics.map((metric) => (
					<Line
						id={metric.value}
						hide={
							!(
								additionalMetrics.includes(metric.value) ||
								mainMetric === metric.value
							)
						}
						strokeOpacity={
							!!hoveringDataKey && hoveringDataKey !== metric.value ? 0.3 : 1
						}
						yAxisId={metric.value}
						key={metric.value}
						dataKey={metric.value}
						stroke={metric.color}
						strokeWidth={mainMetric === metric.value ? 2 : 1}
						dot={false}
						type='linear'
						strokeDasharray={mainMetric === metric.value ? '0' : '5 5'}
					/>
				))}
			</LineChart>
		</ChartContainer>
	)
}
